import { FsClient } from "idfweb-runtime-protocol/fs-rpc";
import type { WorkbenchToRuntime, RuntimeToWorkbench } from "idfweb-runtime-protocol";

type Listener = (message: RuntimeToWorkbench) => void;

/**
 * The workbench's end of the runtime worker's port, with the protocol
 * `idfweb-runtime-protocol` defines. Filesystem replies go to the FsClient;
 * every other message goes to each listener.
 */
export class RuntimeLink {
    readonly fs: FsClient;
    readonly #port: MessagePort;
    readonly #listeners = new Set<Listener>();
    #nextWatch = 1;

    constructor(port: MessagePort) {
        this.#port = port;
        this.fs = new FsClient((message, transfer) => port.postMessage(message, transfer));
        port.onmessage = (event: MessageEvent<RuntimeToWorkbench>) => {
            if (this.fs.receive(event.data)) return;
            for (const listener of this.#listeners) listener(event.data);
        };
        port.start();
    }

    post(message: WorkbenchToRuntime, transfer: Transferable[] = []): void {
        this.#port.postMessage(message, transfer);
    }

    on(listener: Listener): () => void {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }

    /** Ask for `changed` reports under `path`; the returned function stops them. */
    watch(path: string, recursive: boolean): () => void {
        const id = this.#nextWatch++;
        this.post({ type: "watch", id, path, recursive });
        return () => this.post({ type: "unwatch", id });
    }

    /** True if `path` exists in the guest. */
    async exists(path: string): Promise<boolean> {
        try {
            await this.fs.stat(path);
            return true;
        } catch {
            return false;
        }
    }
}
